import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';

import Notification from 'components/shared/Notification';
import type { NotificationItem } from 'stores/notificationStore';
import { useNotificationActions, useNotificationStore } from 'stores/notificationStore';

export type { NotificationItem } from 'stores/notificationStore';

export const useNotifications = useNotificationActions;

const TRANSITION_TIME = 300;

const NotificationWrapper: React.FC<{
  notification: NotificationItem;
  index: number;
  previousHeights: number[];
}> = ({ notification, index, previousHeights }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isNew, setIsNew] = useState(true);
  const [isDismissing, setIsDismissing] = useState(false);
  const { removeNotification } = useNotificationActions();
  const setNotificationHeight = useNotificationStore((state) => state.setNotificationHeight);
  const autoDismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (ref.current) {
      setNotificationHeight(notification.id, ref.current.offsetHeight);
    }
  }, [notification.id, notification.state, notification.title, setNotificationHeight]);

  useEffect(() => {
    const timer = setTimeout(() => setIsNew(false), 50);
    return () => clearTimeout(timer);
  }, []);

  const handleDismiss = () => {
    // Manual dismiss / action click / auto-expiry all funnel here — cancel the pending auto-timer
    // so it can't fire against an already-removed toast.
    if (autoDismissTimerRef.current) {
      clearTimeout(autoDismissTimerRef.current);
      autoDismissTimerRef.current = null;
    }
    notification.onDismiss?.();

    setIsDismissing(true);
    setTimeout(() => {
      removeNotification(notification.id);
    }, TRANSITION_TIME);
  };

  // Auto-dismissing toasts (In-App Notifications) slide themselves out after autoDismissMs; unset
  // for AI-job cards, which are lifecycle-bound and never time-expire. A ref to the latest
  // handleDismiss keeps the effect mount-once (it must NOT reset when the stack re-renders) while
  // still animating through the current dismiss path; unmount clears the timer.
  const dismissRef = useRef(handleDismiss);
  dismissRef.current = handleDismiss;
  useEffect(() => {
    if (!notification.autoDismissMs) return undefined;
    autoDismissTimerRef.current = setTimeout(() => dismissRef.current(), notification.autoDismissMs);
    return () => {
      if (autoDismissTimerRef.current) {
        clearTimeout(autoDismissTimerRef.current);
        autoDismissTimerRef.current = null;
      }
    };
  }, [notification.autoDismissMs]);

  // Self-dismissing toasts (those with autoDismissMs) also slide out on action click — the caller's
  // handler runs first, then the animated dismiss. AI-job cards keep their pass-through handler
  // untouched (their removal is route/lifecycle-driven).
  const handleActionClick = notification.onActionClick
    ? () => {
        notification.onActionClick?.();
        if (notification.autoDismissMs) handleDismiss();
      }
    : undefined;

  const topPosition = previousHeights.slice(0, index).reduce((sum, h) => sum + h + 8, 0);

  return (
    <NotificationWrapperDiv ref={ref} $isNew={isNew} $isDismissing={isDismissing} $top={topPosition}>
      <Notification
        state={notification.state}
        title={notification.title}
        icon={notification.icon}
        timestamp={notification.timestamp}
        onActionClick={handleActionClick}
        onDismiss={handleDismiss}
        actionSuccessText={notification.actionSuccessText}
        actionFailureText={notification.actionFailureText}
      />
    </NotificationWrapperDiv>
  );
};

export const NotificationProvider = () => {
  const notifications = useNotificationStore((state) => state.notifications);
  const notificationHeights = useNotificationStore((state) => state.notificationHeights);

  const previousHeights = notifications.map((n) => notificationHeights[n.id] || 0);

  return (
    <NotificationContainer data-testid='notificationToasts'>
      {notifications.map((notification, index) => (
        <NotificationWrapper
          key={notification.id}
          notification={notification}
          index={index}
          previousHeights={previousHeights}
        />
      ))}
    </NotificationContainer>
  );
};

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(calc(100% + 16px));
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const NotificationContainer = styled.div`
  position: fixed;
  top: 16px;
  right: 16px;
  z-index: 9999;
  pointer-events: none;

  > * {
    pointer-events: auto;
  }
`;

const NotificationWrapperDiv = styled.div<{ $isNew: boolean; $isDismissing: boolean; $top: number }>`
  position: absolute;
  top: ${({ $top }) => $top}px;
  right: 0;
  width: max-content;
  opacity: ${({ $isDismissing }) => ($isDismissing ? 0 : 1)};
  transform: ${({ $isDismissing }) => ($isDismissing ? 'translateX(calc(100% + 16px))' : 'translateX(0)')};
  animation: ${({ $isDismissing }) => ($isDismissing ? 'none' : slideIn)} 0.3s ease-out;
  transition:
    top 0.3s ease-out,
    opacity ${TRANSITION_TIME}ms ease-out,
    transform ${TRANSITION_TIME}ms ease-out;
`;
